import React, { useMemo } from "react";
import { Badge, Button, Card, Col, OverlayTrigger, Row, Tooltip as BsTooltip } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { useHistory } from "react-router-dom";
import {
  DateGapsResponse,
  DateGapStatement,
  getJobTransactionsDateGaps,
  getJobTransactionsDateGapsTagLevel,
} from "../../../../docuclipper/api";
import { useSelector } from "react-redux";
import { ReduxState } from "../../../../redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AgTable from "../AnalyzeTransactionsTable/AgTable";
import moment from "moment";
import { DateRangeFloatingFilter } from "src/views/Docuclipper/AgGrid/DateRangeFloatingFilter";
import { datefilterParamsYYYYMMDD } from "../AnalyzeTransactionsTable";
import {
  CELL_BORDER,
  CELL_COLORS,
  CELL_LEGEND,
  buildCoverageCsv,
  computeCellInfo,
  groupStatementsByAccount,
  monthRange,
  stateLabel,
} from "./coverage";

// Coverage/reconciliation view for accountants and forensic accountants.
// The question this screen must answer in under 5 seconds:
//   "Do I have every statement for every account for every month in this
//    engagement — and is each one reconciled?"
//
// Design decisions:
//   - Rows = accounts, columns = months → scannable grid, not a scatter plot.
//   - Cell state is tri-level (missing / covered-unreconciled / covered-
//     reconciled) with a fourth "transactions-only" state when we see TLs
//     but no statement metadata (indicates a partial import).
//   - Summary chips at top give a quick pass/fail without reading the grid.
//   - Detailed statement table below is the audit trail — every statement
//     shown with its start/end dates, balances, delta, reconciliation status.
//
// Pure helpers (cell state, CSV, month range) live in ./coverage.ts so they
// can be unit-tested without mounting the Redux-connected component.

type Props = {};
export const DateGaps: React.FC<Props> = () => {
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const history = useHistory();
  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );

  // Route to the bank-statement upload flow. Preserves the current l1/l2
  // query params (if present) so the upload lands back in the same project
  // the auditor is working in. The upload UI doesn't accept an account or
  // month hint — it infers those from the uploaded PDF — so we just open
  // the form and let the user drop the file.
  const goToUpload = React.useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    // Strip analyze-specific params that don't belong on the upload page.
    ["mode", "analyzeMode", "docType"].forEach((k) => params.delete(k));
    const qs = params.toString();
    history.push(`/extract/bankMode/${qs ? "?" + qs : ""}`);
  }, [history]);

  const [data, setData] = React.useState<DateGapsResponse>({
    dateGaps: {},
    statements: [],
    summary: {
      totalMonths: 0,
      coveredMonths: 0,
      missingMonths: 0,
      totalStatements: 0,
      reconciledStatements: 0,
      unreconciledStatements: 0,
      perAccount: [],
    },
  });

  const jobId = job ? job.id : "";

  const getGaps = React.useCallback(() => {
    const fn = jobId
      ? getJobTransactionsDateGaps(jobId, startDate, endDate)
      : getJobTransactionsDateGapsTagLevel(l1, l2, startDate, endDate);
    fn.then((rsp) => setData(rsp)).catch(() => null);
  }, [jobId, l1, l2, startDate, endDate]);

  React.useEffect(() => {
    getGaps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, l1, l2]);

  // Auto-populate the date pickers from the discovered range on first load so
  // the grid isn't empty on arrival.
  React.useEffect(() => {
    const { dateGaps, statements } = data;
    if (startDate || endDate) return;
    const months = new Set<string>();
    Object.keys(dateGaps).forEach((a) =>
      Object.keys(dateGaps[a]).forEach((m) => months.add(m))
    );
    statements.forEach((s) => {
      if (s.startDate) months.add(String(s.startDate).slice(0, 7));
      if (s.endDate) months.add(String(s.endDate).slice(0, 7));
    });
    if (months.size === 0) return;
    const sorted = Array.from(months).sort();
    setStartDate(sorted[0] + "-01");
    setEndDate(sorted[sorted.length - 1] + "-01");
  }, [data, startDate, endDate]);

  const accounts: string[] = useMemo(() => {
    const set = new Set<string>();
    Object.keys(data.dateGaps).forEach((a) => set.add(a));
    data.statements.forEach((s) => {
      if (s.accountName) set.add(s.accountName);
    });
    return Array.from(set).filter(Boolean).sort();
  }, [data]);

  const months: string[] = useMemo(() => {
    if (startDate && endDate) {
      return monthRange(startDate.slice(0, 7), endDate.slice(0, 7));
    }
    const set = new Set<string>();
    Object.keys(data.dateGaps).forEach((a) =>
      Object.keys(data.dateGaps[a]).forEach((m) => set.add(m))
    );
    data.statements.forEach((s) => {
      if (s.startDate) set.add(String(s.startDate).slice(0, 7));
      if (s.endDate) set.add(String(s.endDate).slice(0, 7));
    });
    return Array.from(set).sort();
  }, [data, startDate, endDate]);

  // For each (account, month) decide the cell state. A statement covers any
  // month its [startDate, endDate] span touches; TL presence is taken from
  // the legacy `dateGaps` map.
  const statementsByAccount = useMemo(
    () => groupStatementsByAccount(data.statements),
    [data.statements]
  );

  const cellInfo = React.useCallback(
    (account: string, month: string) =>
      computeCellInfo(account, month, data.dateGaps, statementsByAccount),
    [data.dateGaps, statementsByAccount]
  );

  const summary = data.summary;

  // Serialize the coverage grid to CSV for paste-into-workpapers. Logic is in
  // coverage.ts; this wrapper handles the browser download side effect.
  const exportCoverageCsv = React.useCallback(() => {
    if (accounts.length === 0 || months.length === 0) return;
    const csv = buildCoverageCsv(
      accounts,
      months,
      data.dateGaps,
      statementsByAccount,
      summary.perAccount
    );
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `coverage-${moment().format("YYYY-MM-DD")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, months, summary, data, statementsByAccount]);

  const gapMonths = useMemo(() => {
    const out: Array<{ account: string; month: string }> = [];
    accounts.forEach((a) =>
      months.forEach((m) => {
        if (cellInfo(a, m).state === "missing") out.push({ account: a, month: m });
      })
    );
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, months, data]);

  // Statement detail rows for the audit table
  const statementRows = useMemo(() => {
    return data.statements.map((s) => ({
      account: s.accountName,
      chunk: s.chunk,
      startDate: s.startDate ? String(s.startDate).slice(0, 10) : "",
      endDate: s.endDate ? String(s.endDate).slice(0, 10) : "",
      startBalance: s.startBalance,
      endBalance: s.endBalance,
      expectedEndBalance: s.expectedEndBalance,
      delta: s.delta,
      txCount: s.txCount,
      reconciliationStatus: s.reconciliationStatus,
    }));
  }, [data.statements]);

  const statementColumnDefs = [
    { headerName: "Account", field: "account", filter: "agSetColumnFilter", pinned: "left" },
    { headerName: "Chunk", field: "chunk", width: 80 },
    { headerName: "Period start", field: "startDate", filter: "agDateColumnFilter", filterParams: datefilterParamsYYYYMMDD, floatingFilterComponent: DateRangeFloatingFilter, floatingFilterComponentParams: { suppressFilterButton: true }, minWidth: 200 },
    { headerName: "Period end", field: "endDate", filter: "agDateColumnFilter", filterParams: datefilterParamsYYYYMMDD, floatingFilterComponent: DateRangeFloatingFilter, floatingFilterComponentParams: { suppressFilterButton: true }, minWidth: 200 },
    {
      headerName: "Start $",
      field: "startBalance",
      valueFormatter: (p) => (p.value != null ? p.value.toLocaleString() : ""),
    },
    {
      headerName: "End $",
      field: "endBalance",
      valueFormatter: (p) => (p.value != null ? p.value.toLocaleString() : ""),
    },
    {
      headerName: "Balance Diff",
      field: "delta",
      valueFormatter: (p) => (p.value != null ? p.value.toFixed(2) : ""),
      cellStyle: (p) =>
        p.value != null && Math.abs(p.value) > 0.01 ? { color: "#b02a37", fontWeight: 600 } : null,
    },
    { headerName: "Transactions", field: "txCount", width: 120 },
    {
      headerName: "Status",
      field: "reconciliationStatus",
      cellRenderer: (p) => {
        const v = p.value;
        if (v === "reconciled" || v === "matched")
          return (
            <Badge variant="success">
              <FontAwesomeIcon icon="check" /> Reconciled
            </Badge>
          );
        if (v === "mismatch")
          return (
            <Badge variant="danger">
              <FontAwesomeIcon icon="times" /> Mismatch
            </Badge>
          );
        if (v === "unreconciled") return <Badge variant="warning">Not reconciled</Badge>;
        return <Badge variant="secondary">Unknown</Badge>;
      },
    },
  ];

  return (
    <div className="p-4">
      <Row>
        <Col>
          <h2>Date gaps &amp; reconciliation coverage</h2>
          <p className="text-muted mb-0">
            Every month, every account — do you have the statement, and does it reconcile?
            Use this view to spot missing periods before closing the engagement and to
            verify every imported statement matches its stated ending balance.
          </p>
        </Col>
      </Row>

      {/* Summary chips */}
      <Row className="mt-3">
        <Col xs="6" md="3" className="mb-2">
          <Card body>
            <small className="text-muted">Account-months covered</small>
            <h4 className="mb-0">
              {summary.coveredMonths}{" "}
              <small className="text-muted">/ {summary.totalMonths}</small>
            </h4>
            {summary.missingMonths > 0 && (
              <Badge variant="danger" className="mt-1">
                {summary.missingMonths} missing
              </Badge>
            )}
          </Card>
        </Col>
        <Col xs="6" md="3" className="mb-2">
          <Card body>
            <small className="text-muted">Statements reconciled</small>
            <h4 className="mb-0">
              {summary.reconciledStatements}{" "}
              <small className="text-muted">/ {summary.totalStatements}</small>
            </h4>
            {summary.unreconciledStatements > 0 && (
              <Badge variant="warning" className="mt-1">
                {summary.unreconciledStatements} to review
              </Badge>
            )}
          </Card>
        </Col>
        <Col xs="6" md="3" className="mb-2">
          <Card body>
            <small className="text-muted">Accounts</small>
            <h4 className="mb-0">{accounts.length}</h4>
            <small className="text-muted">{summary.perAccount.length} tracked</small>
          </Card>
        </Col>
        <Col xs="6" md="3" className="mb-2">
          <Card body>
            <small className="text-muted">Engagement span</small>
            <h4 className="mb-0">
              {months.length} <small className="text-muted">months</small>
            </h4>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mt-4">
        <Col md="2">
          <Form.Group>
            <Form.Label>Start date</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md="2">
          <Form.Group>
            <Form.Label>End date</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md="2" className="d-flex align-items-end">
          <Button variant="outline-secondary" onClick={getGaps}>Recalculate</Button>
        </Col>
      </Row>

      {/* Legend */}
      <Row className="mt-3">
        <Col>
          <div className="d-flex flex-wrap" style={{ gap: 16 }}>
            {CELL_LEGEND.map((l) => (
              <div key={l.state} className="d-flex align-items-center">
                <div
                  style={{
                    width: 18,
                    height: 18,
                    backgroundColor: CELL_COLORS[l.state],
                    border: `1px solid ${CELL_BORDER[l.state]}`,
                    borderRadius: 3,
                    marginRight: 6,
                  }}
                />
                <small>
                  <strong>{l.label}</strong>
                  <span className="text-muted"> — {l.body}</span>
                </small>
              </div>
            ))}
          </div>
        </Col>
      </Row>

      {/* Heatmap */}
      {accounts.length > 0 && months.length > 0 && (
        <Row className="mt-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h4 className="mb-0">Coverage grid</h4>
              <Button variant="outline-secondary" size="sm" onClick={exportCoverageCsv}>
                <FontAwesomeIcon icon="download" className="mr-1" />
                Export CSV
              </Button>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table
                className="table table-borderless mb-0"
                style={{ borderCollapse: "separate", borderSpacing: 2 }}
              >
                <thead>
                  <tr>
                    <th style={{ minWidth: 180, position: "sticky", left: 0, background: "white" }}>
                      Account
                    </th>
                    {months.map((m) => (
                      <th
                        key={m}
                        style={{
                          writingMode: "vertical-rl",
                          transform: "rotate(180deg)",
                          fontWeight: 400,
                          fontSize: 11,
                          padding: "4px 0",
                          minWidth: 24,
                          textAlign: "center",
                        }}
                      >
                        {m}
                      </th>
                    ))}
                    <th className="text-right" style={{ minWidth: 100 }}>
                      Coverage
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a) => {
                    const perAcc = summary.perAccount.find((p) => p.account === a);
                    return (
                      <tr key={a}>
                        <td
                          style={{
                            fontWeight: 500,
                            position: "sticky",
                            left: 0,
                            background: "white",
                          }}
                        >
                          {a}
                        </td>
                        {months.map((m) => {
                          const info = cellInfo(a, m);
                          const tipBody = (
                            <div style={{ textAlign: "left" }}>
                              <div>
                                <strong>{a}</strong> · {m}
                              </div>
                              <div>Status: {stateLabel(info.state)}</div>
                              {info.statements.map((s) => (
                                <div key={s.chunk} className="mt-1">
                                  <div>
                                    {String(s.startDate).slice(0, 10)} →{" "}
                                    {String(s.endDate).slice(0, 10)}
                                  </div>
                                  {s.startBalance != null && s.endBalance != null && (
                                    <div>
                                      Bal: ${s.startBalance.toLocaleString()} → $
                                      {s.endBalance.toLocaleString()}
                                    </div>
                                  )}
                                  <div>{s.txCount} transactions</div>
                                </div>
                              ))}
                            </div>
                          );
                          return (
                            <OverlayTrigger
                              key={m}
                              placement="top"
                              overlay={<BsTooltip id={`tip-${a}-${m}`}>{tipBody}</BsTooltip>}
                            >
                              <td
                                style={{
                                  backgroundColor: CELL_COLORS[info.state],
                                  border: `1px solid ${CELL_BORDER[info.state]}`,
                                  minWidth: 24,
                                  height: 28,
                                  cursor: "default",
                                }}
                              />
                            </OverlayTrigger>
                          );
                        })}
                        <td className="text-right">
                          <small>
                            {perAcc?.coveragePct ?? 0}% covered
                            <br />
                            <span className="text-muted">
                              {perAcc?.reconciledStatements ?? 0}/{perAcc?.statements ?? 0} reconciled
                            </span>
                          </small>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Col>
        </Row>
      )}

      {/* Gap callout */}
      {gapMonths.length > 0 && (
        <Row className="mt-3">
          <Col>
            <Card body style={{ borderLeft: "4px solid #dc3545" }}>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <strong>
                    <FontAwesomeIcon icon="exclamation-triangle" className="text-danger mr-2" />
                    {gapMonths.length} missing account-month
                    {gapMonths.length === 1 ? "" : "s"}
                  </strong>
                  <div className="text-muted small">
                    Upload the missing statements to close the gaps.
                  </div>
                </div>
                <Button variant="primary" size="sm" onClick={goToUpload}>
                  <FontAwesomeIcon icon="upload" className="mr-1" />
                  Upload statement
                </Button>
              </div>
              <div className="mt-2">
                {gapMonths.slice(0, 20).map((g, i) => (
                  <Badge key={i} variant="light" className="mr-2 mb-1" style={{ border: "1px solid #dc3545" }}>
                    {g.account} · {g.month}
                  </Badge>
                ))}
                {gapMonths.length > 20 && (
                  <small className="text-muted">…and {gapMonths.length - 20} more</small>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Statement audit table */}
      {statementRows.length > 0 && (
        <Row className="mt-4">
          <Col>
            <h4>Statements</h4>
            <p className="text-muted">
              One row per imported statement period. Sort or filter by status to triage
              what still needs reconciliation.
            </p>
            <AgTable
              hasExport={true}
              columnDefs={statementColumnDefs}
              rowData={statementRows}
              height={400}
              width="100%"
            />
          </Col>
        </Row>
      )}

      {accounts.length === 0 && (
        <Row className="mt-4">
          <Col>
            <Card body className="text-center text-muted">
              No statements or transactions found for this project yet. Upload a bank
              statement to populate this view.
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};
export default DateGaps;
